import { defineComponent, ref, computed, inject, onMounted, watch, set } from 'vue';
import { useLocalizationSupport } from '../../composables/localization-support/index.js';
import { useStoreModule } from '../../composables/store-module/index.js';
import UiAssetClassBadge from '../ui-asset-class-badge/UiAssetClassBadge.vue.js';
import { searchSuggesterFactory } from '../search-suggester/setup/index.js';
import { watchlistSelectorFactory } from '../watchlist-selector/setup/index.js';
import HoistComponent from '../hoist-component/HoistComponent.vue.js';
import { marketSelectorFactory } from '../market-selector/setup/index.js';

var _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "WatchlistPositionAdd",
  props: {
    locale: { type: null, required: true },
    suggesterProps: { type: null, required: false },
    ids: { type: Array, required: false },
    hideSuggester: { type: Boolean, required: false, default: false },
    defaultWatchlistId: { type: null, required: false }
  },
  emits: ["store-error", "data-load", "on-positions-added", "submit", "cancel"],
  setup(__props, { emit: emits }) {
    var _a;
    const props = __props;
    const watchlistId = ref(props.defaultWatchlistId);
    const selectedIds = ref(structuredClone((_a = props.ids) != null ? _a : []));
    const action = ref(null);
    const componentInputs = computed(() => ({
      language: props.locale,
      selectedIds: selectedIds.value,
      watchlistId: watchlistId.value,
      defaultWatchlistId: props.defaultWatchlistId,
      action: action.value,
      suggesterProps: props.suggesterProps
    }));
    const positions = computed(() => {
      var _a2, _b;
      return (_b = (_a2 = storeData.value) == null ? void 0 : _a2.positions) != null ? _b : [];
    });
    const validationErrors = computed(() => {
      var _a2, _b;
      return (_b = (_a2 = storeMeta.value) == null ? void 0 : _a2.validationErrors) != null ? _b : [];
    });
    const selectedIdsLength = computed(() => {
      return selectedIds.value.length;
    });
    const hasValidInput = computed(() => {
      return !isLoadingStoreData.value && validationErrors.value.length === 0 && selectedIds.value.length > 0 && !!watchlistId.value;
    });
    const translator = inject("translator");
    const numberFormatter = inject("numberFormatter");
    const dateFormatter = inject("dateFormatter");
    const localizationSupport = useLocalizationSupport(props, { translator, numberFormatter, dateFormatter });
    const { translate, textFormat } = localizationSupport;
    const injectedStoreModule = inject("storeModule");
    const storeModule = useStoreModule(
      {
        storeModule: injectedStoreModule
      },
      componentInputs,
      emits,
      { requestDataOnComponentInputsChange: false }
    );
    const { storeData, storeMeta, storeRequestData, isLoadingStoreData } = storeModule;
    const watchlistHasPosition = (id) => {
      return validationErrors.value.find((error) => JSON.stringify(error.id) === JSON.stringify(id)) !== void 0;
    };
    const getAssetClass = (position) => {
      var _a2;
      return (_a2 = position.assetClass) != null ? _a2 : void 0;
    };
    const onWatchlistChange = async (changedWatchlistId) => {
      watchlistId.value = changedWatchlistId;
      await updateWatchlistPositions();
    };
    const updateWatchlistPositions = async () => {
      action.value = "getPositions";
      await storeRequestData();
    };
    const onSelectResult = async (item) => {
      if (!selectedIds.value.includes(item.id)) {
        selectedIds.value.push(item.id);
        await updatedSelectedIdsData();
      }
    };
    const onMarketChange = async (index, market) => {
      if (market) {
        set(selectedIds.value, index, market.id);
        await updatedSelectedIdsData();
      }
    };
    const onSubmit = async () => {
      if (hasValidInput.value) {
        action.value = "addPositions";
        const status = await storeRequestData();
        if (status) {
          emits("on-positions-added", selectedIds.value);
        }
        action.value = null;
        selectedIds.value.splice(0);
        emits("submit");
      }
    };
    const onCancel = () => {
      selectedIds.value.splice(0);
      emits("cancel");
    };
    const onPositionRemove = (index) => {
      selectedIds.value.splice(index, 1);
    };
    const updatedSelectedIdsData = async () => {
      action.value = null;
      await storeRequestData();
    };
    onMounted(() => {
      void updateWatchlistPositions().then(updatedSelectedIdsData);
    });
    watch(
      () => props.locale,
      () => {
        void storeRequestData();
      }
    );
    watch(
      () => props.ids,
      () => {
        var _a2;
        selectedIds.value = structuredClone((_a2 = props.ids) != null ? _a2 : []);
        void updatedSelectedIdsData();
      }
    );
    const WatchlistSelector = watchlistSelectorFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().watchlistSelector : void 0
    });
    const SearchSuggester = searchSuggesterFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().searchSuggester : void 0
    });
    const MarketSelector = marketSelectorFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().marketSelector : void 0
    });
    return { __sfc: true, props, emits, watchlistId, selectedIds, action, componentInputs, positions, validationErrors, selectedIdsLength, hasValidInput, translator, numberFormatter, dateFormatter, localizationSupport, translate, textFormat, injectedStoreModule, storeModule, storeData, storeMeta, storeRequestData, isLoadingStoreData, watchlistHasPosition, getAssetClass, onWatchlistChange, updateWatchlistPositions, onSelectResult, onMarketChange, onSubmit, onCancel, onPositionRemove, updatedSelectedIdsData, WatchlistSelector, SearchSuggester, MarketSelector, UiAssetClassBadge, HoistComponent };
  }
});

export { _sfc_main as default };
